import { Button, Result, Typography } from "antd";
import { Component, type ErrorInfo, type ReactNode } from "react";
import { useRouteError } from "react-router";
import { useI18n } from "@/i18n";

/** A short id the user can quote to support; it is also in the console log. */
function newReference(): string {
  return Math.random().toString(36).slice(2, 10).toUpperCase();
}

function CrashNotice({ reference, onRetry }: { reference: string; onRetry: () => void }) {
  const { t } = useI18n();
  return (
    <Result
      status="error"
      title={t("page.error.title")}
      subTitle={
        <>
          {t("error.generic")}
          <br />
          <Typography.Text type="secondary" copyable={{ text: reference }}>
            {t("common.reference", { id: reference })}
          </Typography.Text>
        </>
      }
      extra={
        <Button type="primary" onClick={onRetry}>
          {t("common.retry")}
        </Button>
      }
    />
  );
}

type State = { error: Error | null; reference: string };

/**
 * Catches a crash inside one work tab, so the other tabs keep working
 * (App Flow §12: friendly message, retry, reference code).
 */
export class PageErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null, reference: "" };

  static getDerivedStateFromError(error: Error): State {
    return { error, reference: newReference() };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(`[page crash ${this.state.reference}]`, error, info.componentStack);
  }

  render() {
    if (this.state.error) {
      return <CrashNotice reference={this.state.reference} onRetry={() => this.setState({ error: null, reference: "" })} />;
    }
    return this.props.children;
  }
}

/** errorElement for the router: replaces React Router's developer screen. */
export function RouteError() {
  const error = useRouteError();
  const reference = newReference();
  console.error(`[route error ${reference}]`, error);
  return (
    <div style={{ minHeight: "100vh", display: "grid", placeItems: "center" }}>
      <CrashNotice reference={reference} onRetry={() => window.location.reload()} />
    </div>
  );
}
